// WHO SEES WHICH SURFACE, DECIDED IN ONE PLACE.
//
// From the 4 October call (§7 of docs/clinical-feedback-2026-10-04-annan.md). Her named
// adoption risk is overwhelm, said twice, and the consequence she named is specific:
//
//   "it did so much — which was great. But at the same time it was very overwhelming when
//    you look at everything on the screen... it could lead to NON-USAGE OR UNDER-USAGE."
//
// The mitigation she endorsed, in her words — "that is going to be key" — is role-based
// views, with the example she chose herself:
//
//   a staff nurse does not need the MDS surface, because "they're not doing MDS"
//   a unit manager gets residents at risk and the 24-hour check
//   a DON or administrator gets the building
//
// ── WHY A MODULE AND NOT THREE MORE INLINE TERNARIES ─────────────────────────
//
// Because the nav and the renderer were already disagreeing, and nothing reported it.
//
// `facilities` renders only for admin / vp / canEdit, and its nav ENTRY was ungated — with a
// trailing comment claiming "visible to admin, vp, regional" that the code beside it did not
// do. So a staff nurse saw Facilities in the sidebar, clicked it, and got a blank screen: not
// a refusal, not an explanation, nothing. A permission expressed twice in two places is a
// permission that will eventually be expressed two different ways, and honest prose beside a
// wrong state is this product's signature defect.
//
// So both the sidebar and the render guard ask this file. They cannot drift apart, because
// there is only one answer.
//
// ── WHAT THIS IS NOT ─────────────────────────────────────────────────────────
//
// NOT a security boundary. Hiding a nav item is an overwhelm fix, not an access control: the
// engine scopes every clinical read by org_uuid server-side and refuses what the caller may
// not have, and that is what actually protects resident data. This file makes the screen
// smaller for people whose job it is not. Treating it as the protection would be the mistake.
//
// NOT a new role taxonomy. The roles already exist in App.js's ROLES and the job titles map
// onto them (MDS coordinators sign in as `editor`, which is why the MDS surface stays visible
// to them; NHA/DON/ADON map to `facility_admin`). Inventing a parallel set of tiers to express
// three decisions would be a bigger change than the three decisions.

/**
 * Surfaces that are nursing-LEADERSHIP work: planning the building, chasing revenue, or
 * forecasting citations. §8 of the feedback doc asked directly who the product is for and
 * the answer was "nursing leadership — unit managers, ADONs, DONs, administrators".
 *
 * A bedside nurse is not excluded from the product by these; they are excluded from three
 * screens that would never have told them anything they could act on, on a sidebar whose
 * length is the thing she warned about.
 */
export const LEADERSHIP_SURFACES = Object.freeze({
  // HER OWN EXAMPLE. A revenue queue of MDS coding gaps, for the MDS coordinator.
  "scout-queue": "MDS Opportunities",
  // The proactive citation predictor. `staff` cannot create a POC at all (ROLES.staff
  // has canEdit: false), so this screen was already a read-only view of work the reader
  // was not able to do.
  "poc-drafts": "POC Drafts",
  // Building-level forecasting. "A DON or administrator gets the building."
  "predictive-analytics": "Predictive Analytics",
});

/** The roles that are view-only at a facility: a bedside nurse and a read-only observer. */
const BEDSIDE_ROLES = Object.freeze(["staff", "viewer"]);

/**
 * Is this surface on this session's screen?
 *
 * UNKNOWN IS HIDDEN FOR A LEADERSHIP SURFACE AND VISIBLE OTHERWISE, which is not a
 * contradiction: the question being asked is "should this be on the screen", and the safe
 * answer for a screen nobody listed is to leave it where it was. Only the surfaces this file
 * names change behaviour. A missing or malformed session, though, is treated as bedside —
 * an absent role must not read as a privileged one.
 *
 * @param {{role?: string}|null|undefined} session
 * @param {string} surfaceId  the nav item's `id`
 * @returns {boolean}
 */
export function canViewSurface(session, surfaceId) {
  const role = session && typeof session.role === "string" ? session.role : null;
  if (surfaceId === "facilities") {
    // THE RULE THE RENDERER ALREADY HAD, moved here verbatim rather than redesigned:
    // admin, vp, or anyone who can edit (facility_admin, regional, editor). Changing who
    // may reach Facilities is a separate decision from making the sidebar agree with the
    // renderer, and bundling the two would make this commit unreviewable.
    return role === "admin" || role === "vp" || CAN_EDIT_ROLES.includes(role);
  }
  if (Object.prototype.hasOwnProperty.call(LEADERSHIP_SURFACES, surfaceId)) {
    return !!role && !BEDSIDE_ROLES.includes(role);
  }
  return true;
}

/**
 * The roles whose ROLES entry carries canEdit: true.
 *
 * Duplicated from App.js's ROLES deliberately and asserted against it by the test, rather
 * than imported: ROLES lives inside a 50,000-line module that pulls in the whole app, and a
 * nav helper that cannot be unit-tested without booting the app is a helper that will not be
 * unit-tested. The test is what stops the copy drifting.
 */
export const CAN_EDIT_ROLES = Object.freeze(["admin", "regional", "vp", "facility_admin", "editor"]);
